import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CircleAlert, Navigation, RadioTower } from 'lucide-react';
import Select from 'react-select';
import useMachinesOverview from '../hooks/useMachinesOverview';
import { getDirectionsUrl } from '../utils/mapUtils';
import MachineStatusOption, { getMachineStatus } from './MachineStatusOption';
import CompanyScopePicker, { NO_COMPANY } from './CompanyScopePicker';
import styles from './MachineSelector.module.scss';

function MachineSelector({
  selectedDevice,
  setSelectedDevice,
  selectedCompanyIds,
  setSelectedCompanyIds,
}) {
  const [internalSelectedDevice, setInternalSelectedDevice] = useState(selectedDevice);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedDevice) {
      setInternalSelectedDevice(selectedDevice);
    }
  }, [selectedDevice]);

  const overview = useMachinesOverview();
  const overviewMachines = useMemo(() => overview?.machines ?? [], [overview]);

  const machines = useMemo(
    () =>
      overviewMachines.map(machine => ({
        id: machine.id,
        serial: machine.serial_number,
        nickname: machine.nickname || machine.serial_number,
        companyId: machine.company_id,
        companyName: machine.company_name,
        isOnline: Boolean(machine.is_online),
        inOperation: Boolean(machine.in_operation),
        incidentCount: machine.untaken_incident_count ?? 0,
        location: machine.location,
      })),
    [overviewMachines]
  );

  useEffect(() => {
    if (machines.length === 0) {
      return;
    }
    if (!internalSelectedDevice) {
      const firstSerial = machines[0].serial;
      setInternalSelectedDevice(firstSerial);
      setSelectedDevice?.(firstSerial);
    }
  }, [machines, internalSelectedDevice, setSelectedDevice]);

  const handleDeviceChange = selectedOption => {
    if (!selectedOption) {
      return;
    }

    const newSerial = selectedOption.value;
    setInternalSelectedDevice(newSerial);
    setSelectedDevice?.(newSerial);
  };

  const filteredMachines =
    selectedCompanyIds.length === 0
      ? machines
      : machines.filter(machine => selectedCompanyIds.includes(machine.companyId || NO_COMPANY));

  const selectOptions = filteredMachines.map(machine => {
    const status = getMachineStatus(machine);
    return {
      value: machine.serial,
      label: [machine.nickname, machine.serial, machine.companyName, status.label]
        .filter(Boolean)
        .join(' · '),
      machine: {
        nickname: machine.nickname,
        serialNumber: machine.serial,
        companyName: machine.companyName,
        isOnline: machine.isOnline,
        inOperation: machine.inOperation,
      },
    };
  });

  const selectedOption =
    selectOptions.find(option => option.value === internalSelectedDevice) || null;
  const selectedMachine = machines.find(machine => machine.serial === internalSelectedDevice);

  const statusLabel = !selectedMachine?.isOnline
    ? 'Offline'
    : selectedMachine.inOperation
      ? 'In operation'
      : 'Online · Idle';

  const directionsUrl = getDirectionsUrl(selectedMachine?.location);

  const statusClass = !selectedMachine?.isOnline
    ? styles.statusOffline
    : selectedMachine.inOperation
      ? styles.statusActive
      : styles.statusIdle;

  return (
    <section className={styles.hubDevicesPanel}>
      <header className={styles.panelHeader}>
        <span className={styles.headerIcon}>
          <RadioTower size={18} />
        </span>
        <div>
          <span className={styles.eyebrow}>Hub Workspace</span>
          <h2 className={styles.panelTitle}>Find a machine</h2>
        </div>
      </header>

      <div className={styles.deviceSelection}>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Company scope</span>
          <CompanyScopePicker
            selectedCompanyIds={selectedCompanyIds}
            setSelectedCompanyIds={setSelectedCompanyIds}
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="machine-context-select">
            Machine
          </label>
          <Select
            inputId="machine-context-select"
            classNamePrefix="machineSelect"
            options={selectOptions}
            value={selectedOption}
            onChange={handleDeviceChange}
            formatOptionLabel={(option, { context }) => (
              <MachineStatusOption machine={option.machine} compact={context === 'value'} />
            )}
            isSearchable
            placeholder="Search by name, serial or company…"
            noOptionsMessage={() => 'No machines found'}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPlacement="auto"
            maxMenuHeight={9999}
          />
        </div>

        {selectedMachine && (
          <div className={styles.selectedMachine}>
            <div className={styles.machineIdentity}>
              <span className={`${styles.statusDot} ${statusClass}`} />
              <div>
                <strong>{selectedMachine.nickname}</strong>
                <span>{selectedMachine.serial}</span>
              </div>
            </div>
            <div className={styles.machineMeta}>
              <span>{statusLabel}</span>
              {selectedMachine.companyName && <span>{selectedMachine.companyName}</span>}
              {selectedMachine.incidentCount > 0 && (
                <span className={styles.incidentBadge}>
                  <CircleAlert size={13} />
                  {selectedMachine.incidentCount} open
                </span>
              )}
            </div>
          </div>
        )}

        {selectedMachine && (
          <button
            type="button"
            className={styles.openWorkspaceButton}
            onClick={() => navigate(`/device/${selectedMachine.serial}/status`)}
          >
            <span>
              <strong>View machine</strong>
              <small>Open status and machine tools</small>
            </span>
            <ArrowRight size={18} />
          </button>
        )}

        {selectedMachine &&
          (directionsUrl ? (
            <a
              className={styles.navigateButton}
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Navigation size={18} className={styles.navigateIcon} />
              <span>
                <strong>Navigate to this machine</strong>
                <small>Open last known position in Google Map</small>
              </span>
            </a>
          ) : (
            <div className={`${styles.navigateButton} ${styles.navigateDisabled}`} aria-disabled>
              <Navigation size={18} className={styles.navigateIcon} />
              <span>
                <strong>Navigate to this machine</strong>
                <small>No known position</small>
              </span>
            </div>
          ))}
      </div>
    </section>
  );
}

export default MachineSelector;
